import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import esgTrustScoreAnalysisService from '../../../services/esg/esgTrustScoreAnalysisService';

const ESGTrustScoreAnalysis = () => {
  const renderIcon = (icon, size = 24) =>
    icon === 'handshake' ? <HandshakeIcon size={size} /> : icon;

  // State Management
  const [activeTab, setActiveTab] = useState('esg-overview');
  const [trustScoreOverview, setTrustScoreOverview] = useState({});
  const [scoreComponents, setScoreComponents] = useState([]);
  const [riskAssessment, setRiskAssessment] = useState({});
  const [complianceStatus, setComplianceStatus] = useState({});
  const [securityMetrics, setSecurityMetrics] = useState({});
  const [benchmarks, setBenchmarks] = useState({});
  const [auditTrail, setAuditTrail] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [trendAnalysis, setTrendAnalysis] = useState({});
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [esgPillar, setEsgPillar] = useState('environmental');

  useEffect(() => {
    const loadData = () => {
      setTrustScoreOverview(esgTrustScoreAnalysisService.getTrustScoreOverview());
      setScoreComponents(esgTrustScoreAnalysisService.getScoreComponents());
      setRiskAssessment(esgTrustScoreAnalysisService.getRiskAssessment());
      setComplianceStatus(esgTrustScoreAnalysisService.getComplianceStatus());
      setSecurityMetrics(esgTrustScoreAnalysisService.getSecurityMetrics());
      setBenchmarks(esgTrustScoreAnalysisService.getBenchmarks());
      setAuditTrail(esgTrustScoreAnalysisService.getAuditTrail());
      setRecommendations(esgTrustScoreAnalysisService.getRecommendations());
      setTrendAnalysis(esgTrustScoreAnalysisService.getTrendAnalysis());
      setLoading(false);
    };

    loadData();
    
    const unsubscribe = esgTrustScoreAnalysisService.subscribe(loadData);
    return unsubscribe;
  }, []);

  // Tab Navigation
  const tabs = [
    { id: 'esg-overview', name: 'ESG Principles Overview', icon: '🌍' },
    { id: 'environmental', name: 'Environmental Impact', icon: '🌱' },
    { id: 'social', name: 'Social Responsibility', icon: '👥' },
    { id: 'governance', name: 'Governance Standards', icon: '⚖️' },
    { id: 'trust-matrix', name: 'Trust Matrix', icon: '🛡️' },
    { id: 'stakeholder', name: 'Stakeholder Engagement', icon: 'handshake' },
    { id: 'sustainability', name: 'Sustainability Metrics', icon: '♻️' },
    { id: 'action-plan', name: 'Action Plan', icon: '📋' }
  ];

  // ESG Pillars
  const esgPillars = [
    {
      id: 'environmental',
      name: 'Environmental',
      icon: '🌱',
      color: 'from-green-500 to-emerald-600',
      description: 'Carbon footprint, energy efficiency, waste management, and climate impact'
    },
    {
      id: 'social',
      name: 'Social',
      icon: '👥',
      color: 'from-blue-500 to-indigo-600',
      description: 'Labor practices, diversity & inclusion, community engagement, human rights'
    },
    {
      id: 'governance',
      name: 'Governance',
      icon: '⚖️',
      color: 'from-purple-500 to-pink-600',
      description: 'Corporate ethics, transparency, compliance, board oversight, risk management'
    }
  ];

  // Utility Functions
  const getGradeColor = (grade) => {
    if (grade?.startsWith('A')) return 'text-green-600';
    if (grade?.startsWith('B')) return 'text-blue-600';
    if (grade?.startsWith('C')) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'Critical': return 'bg-red-100 text-red-800';
      case 'High': return 'bg-orange-100 text-orange-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'text-green-600';
      case 'good': return 'text-blue-600';
      case 'adequate': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return '📈';
      case 'declining': return '📉';
      case 'stable': return '➡️';
      default: return '➡️';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading ESG Trust Score Analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Header with ESG Branding */}
        <div className="mb-8 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-2xl shadow-2xl p-8 text-white">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-5xl">🌍</span>
                <h1 className="text-4xl font-bold">ESG Principles Framework</h1>
              </div>
              <p className="text-green-100 text-lg max-w-3xl">
                Environmental, Social & Governance Assessment - Building Trust Through Sustainable and Ethical Practices
              </p>
            </div>
            <div className="flex items-center space-x-6 bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-center border-r border-white/20 pr-6">
                <div className="text-5xl font-bold text-white">
                  {trustScoreOverview.overallScore || 87}
                </div>
                <div className="text-sm text-green-100 mt-1">ESG Score</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white">
                  {trustScoreOverview.grade || 'A-'}
                </div>
                <div className="text-sm text-green-100 mt-1">Rating</div>
              </div>
            </div>
          </div>
          
          {/* ESG Pillars Quick Stats */}
          <div className="grid grid-cols-3 gap-4 mt-6">
            {esgPillars.map(pillar => (
              <div key={pillar.id} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{pillar.icon}</span>
                    <span className="font-semibold">{pillar.name}</span>
                  </div>
                  <span className="text-2xl font-bold">{Math.round(Math.random() * 10 + 85)}</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white rounded-full" style={{width: `${Math.round(Math.random() * 20 + 80)}%`}}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-xl shadow-lg p-2 flex space-x-2 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium rounded-lg transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{renderIcon(tab.icon, 18)}</span>
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        {/* ESG Principles Overview Tab */}
        {activeTab === 'esg-overview' && (
          <div className="space-y-6">
            {/* ESG Pillars Detailed Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {esgPillars.map(pillar => (
                <div key={pillar.id} className="bg-white rounded-xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow">
                  <div className={`bg-gradient-to-r ${pillar.color} p-6 text-white`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-5xl">{pillar.icon}</span>
                      <div className="text-right">
                        <div className="text-3xl font-bold">{Math.round(Math.random() * 10 + 85)}</div>
                        <div className="text-sm opacity-90">Score</div>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-2">{pillar.name}</h3>
                    <p className="text-sm opacity-90">{pillar.description}</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Performance Trend</span>
                      <span className="text-green-600 font-semibold flex items-center gap-1">
                        ↗ Improving
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Industry Rank</span>
                      <span className="font-bold text-blue-600">Top 15%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Compliance</span>
                      <span className="font-bold text-green-600">98%</span>
                    </div>
                    <button 
                      onClick={() => setActiveTab(pillar.id)}
                      className={`w-full mt-4 py-2 rounded-lg bg-gradient-to-r ${pillar.color} text-white font-semibold hover:opacity-90 transition-opacity`}
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Key ESG Metrics Dashboard */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Carbon Footprint</p>
                    <p className="text-3xl font-bold text-green-600">-23%</p>
                    <p className="text-gray-500 text-sm">vs last year</p>
                  </div>
                  <div className="text-4xl">🌱</div>
                </div>
                <div className="mt-2 text-xs text-green-600 font-medium">
                  ↗ On track to net-zero by 2030
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Diversity & Inclusion</p>
                    <p className="text-3xl font-bold text-blue-600">92%</p>
                    <p className="text-gray-500 text-sm">representation</p>
                  </div>
                  <div className="text-4xl">�</div>
                </div>
                <div className="mt-2 text-xs text-blue-600 font-medium">
                  ↗ +8% improvement YoY
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Board Independence</p>
                    <p className="text-3xl font-bold text-purple-600">85%</p>
                    <p className="text-gray-500 text-sm">independent directors</p>
                  </div>
                  <div className="text-4xl">⚖️</div>
                </div>
                <div className="mt-2 text-xs text-purple-600 font-medium">
                  ✓ Exceeds industry standard
                </div>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-teal-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm font-medium">Stakeholder Trust</p>
                    <p className="text-3xl font-bold text-teal-600">4.7/5</p>
                    <p className="text-gray-500 text-sm">satisfaction rating</p>
                  </div>
                  <HandshakeIcon size={40} />
                </div>
                <div className="mt-2 text-xs text-teal-600 font-medium">
                  ↗ 94% confidence level
                </div>
              </div>
            </div>

            {/* UN SDG Alignment */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>🎯</span>
                UN Sustainable Development Goals Alignment
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {[
                  { num: 3, name: 'Good Health', score: 95 },
                  { num: 7, name: 'Clean Energy', score: 88 },
                  { num: 8, name: 'Decent Work', score: 92 },
                  { num: 10, name: 'Reduced Inequalities', score: 87 },
                  { num: 12, name: 'Responsible Consumption', score: 90 },
                  { num: 13, name: 'Climate Action', score: 85 }
                ].map(goal => (
                  <div key={goal.num} className="border border-gray-200 rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                    <div className="text-3xl font-bold text-blue-600 mb-1">SDG {goal.num}</div>
                    <div className="text-xs text-gray-600 mb-2">{goal.name}</div>
                    <div className="text-lg font-bold text-green-600">{goal.score}%</div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                      <div className="bg-green-500 h-1.5 rounded-full" style={{width: `${goal.score}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            

            {/* Score Breakdown Chart */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Trust Score Components</h3>
              <div className="space-y-4">
                {scoreComponents.map((component, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-800">{component.category}</span>
                        <span className="text-gray-500 text-sm">({component.weight}%)</span>
                        <span className="text-xl">{getTrendIcon(component.trend)}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`font-bold ${getGradeColor(component.grade)}`}>
                          {component.currentScore}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          component.grade.startsWith('A') ? 'bg-green-100 text-green-800' :
                          component.grade.startsWith('B') ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {component.grade}
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className={`h-3 rounded-full ${
                          component.grade.startsWith('A') ? 'bg-green-500' :
                          component.grade.startsWith('B') ? 'bg-blue-500' :
                          'bg-yellow-500'
                        }`}
                        style={{ width: `${component.currentScore / 10}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <button 
                onClick={() => setActiveTab('risks')}
                className="p-6 bg-white border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-colors text-left"
              >
                <div className="text-3xl mb-3">⚠️</div>
                <h4 className="font-bold text-gray-800 mb-2">View Risk Assessment</h4>
                <p className="text-gray-600 text-sm">Review {riskAssessment.totalRisks} identified risks</p>
              </button>
              
              <button 
                onClick={() => setActiveTab('recommendations')}
                className="p-6 bg-white border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
              >
                <div className="text-3xl mb-3">💡</div>
                <h4 className="font-bold text-gray-800 mb-2">View Recommendations</h4>
                <p className="text-gray-600 text-sm">Explore improvement opportunities</p>
              </button>
              
              <button 
                onClick={() => setActiveTab('compliance')}
                className="p-6 bg-white border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-300 transition-colors text-left"
              >
                <div className="text-3xl mb-3">✅</div>
                <h4 className="font-bold text-gray-800 mb-2">Compliance Status</h4>
                <p className="text-gray-600 text-sm">Review regulatory compliance</p>
              </button>
            </div>
          </div>
        )}

        {/* Environmental Impact Tab */}
        {activeTab === 'environmental' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <span>🌱</span>
                Environmental Impact & Sustainability
              </h2>
              <p className="text-green-100">Measuring our commitment to environmental stewardship and climate action</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Carbon Emissions</h3>
                  <span className="text-3xl">🏭</span>
                </div>
                <div className="text-4xl font-bold text-green-600 mb-2">-23%</div>
                <p className="text-gray-600 text-sm mb-4">Reduction vs 2023 baseline</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scope 1:</span>
                    <span className="font-semibold">450 tCO2e</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scope 2:</span>
                    <span className="font-semibold">1,200 tCO2e</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Scope 3:</span>
                    <span className="font-semibold">3,500 tCO2e</span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="text-xs text-green-600 font-medium">✓ Net-zero target: 2030</div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Renewable Energy</h3>
                  <span className="text-3xl">⚡</span>
                </div>
                <div className="text-4xl font-bold text-yellow-600 mb-2">78%</div>
                <p className="text-gray-600 text-sm mb-4">From renewable sources</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Solar</span>
                      <span className="font-semibold">45%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{width: '45%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Wind</span>
                      <span className="font-semibold">25%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{width: '25%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Hydro</span>
                      <span className="font-semibold">8%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-teal-500 h-2 rounded-full" style={{width: '8%'}}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Waste Management</h3>
                  <span className="text-3xl">♻️</span>
                </div>
                <div className="text-4xl font-bold text-teal-600 mb-2">85%</div>
                <p className="text-gray-600 text-sm mb-4">Diverted from landfill</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recycled:</span>
                    <span className="font-semibold text-green-600">65%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Composted:</span>
                    <span className="font-semibold text-green-600">15%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reused:</span>
                    <span className="font-semibold text-green-600">5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Landfill:</span>
                    <span className="font-semibold text-red-600">15%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Water Conservation</h3>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-3xl font-bold text-blue-600">-18%</div>
                    <p className="text-gray-600 text-sm">Water consumption reduction</p>
                  </div>
                  <span className="text-5xl">💧</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Usage:</span>
                    <span className="font-semibold">45,000 m³/year</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Recycled Water:</span>
                    <span className="font-semibold text-teal-600">35%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rainwater Harvested:</span>
                    <span className="font-semibold text-blue-600">8,500 m³</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Biodiversity Initiatives</h3>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-3xl font-bold text-green-600">12</div>
                    <p className="text-gray-600 text-sm">Active programs</p>
                  </div>
                  <span className="text-5xl">🦋</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <span>🌳</span>
                    <span>15,000 trees planted in 2024</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span>🐝</span>
                    <span>Pollinator garden program</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span>🌾</span>
                    <span>Native habitat restoration</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span>🐦</span>
                    <span>Wildlife corridor protection</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Social Responsibility Tab */}
        {activeTab === 'social' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <span>👥</span>
                Social Responsibility & Human Capital
              </h2>
              <p className="text-blue-100">Empowering people, strengthening communities, and promoting equity</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="text-4xl mb-2">👨‍👩‍👧‍👦</div>
                <div className="text-3xl font-bold text-blue-600">8,542</div>
                <p className="text-gray-600 text-sm">Total Employees</p>
                <div className="mt-3 text-xs text-green-600 font-medium">↗ +12% growth</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="text-4xl mb-2">👩‍💼</div>
                <div className="text-3xl font-bold text-purple-600">48%</div>
                <p className="text-gray-600 text-sm">Women in Leadership</p>
                <div className="mt-3 text-xs text-green-600 font-medium">✓ Above target</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="text-4xl mb-2">🎓</div>
                <div className="text-3xl font-bold text-indigo-600">92%</div>
                <p className="text-gray-600 text-sm">Training Participation</p>
                <div className="mt-3 text-xs text-green-600 font-medium">↗ +5% YoY</div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-6 text-center">
                <div className="text-4xl mb-2">⭐</div>
                <div className="text-3xl font-bold text-yellow-600">4.6/5</div>
                <p className="text-gray-600 text-sm">Employee Satisfaction</p>
                <div className="mt-3 text-xs text-green-600 font-medium">↗ Improving</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>🌈</span>
                  Diversity & Inclusion Metrics
                </h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Gender Diversity</span>
                      <span className="font-semibold">52% / 48%</span>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden">
                      <div className="bg-pink-500" style={{width: '52%'}}></div>
                      <div className="bg-blue-500" style={{width: '48%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Ethnic Diversity</span>
                      <span className="font-semibold">65%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-purple-500 h-3 rounded-full" style={{width: '65%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Age Diversity (3+ generations)</span>
                      <span className="font-semibold">78%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-indigo-500 h-3 rounded-full" style={{width: '78%'}}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Disability Inclusion</span>
                      <span className="font-semibold">8.5%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-teal-500 h-3 rounded-full" style={{width: '42.5%'}}></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>💰</span>
                  Fair Compensation & Benefits
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Living Wage Compliance:</span>
                    <span className="font-bold text-green-600">100%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Pay Equity Ratio (M/F):</span>
                    <span className="font-bold text-green-600">1:0.98</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Benefits Coverage:</span>
                    <span className="font-bold text-green-600">95%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Retirement Plan Participation:</span>
                    <span className="font-bold text-blue-600">88%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Health Insurance:</span>
                    <span className="font-bold text-green-600">98%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Paid Parental Leave:</span>
                    <span className="font-bold text-green-600">16 weeks</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <HandshakeIcon size={22} />
                Community Engagement & Impact
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-3xl font-bold text-blue-600 mb-1">$2.5M</div>
                  <p className="text-gray-600 text-sm mb-3">Community Investment</p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>• Education programs</div>
                    <div>• Healthcare initiatives</div>
                    <div>• Infrastructure development</div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-3xl font-bold text-purple-600 mb-1">12,500</div>
                  <p className="text-gray-600 text-sm mb-3">Volunteer Hours</p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>• 85% employee participation</div>
                    <div>• 45 partner organizations</div>
                    <div>• 3 countries</div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-3xl font-bold text-green-600 mb-1">450</div>
                  <p className="text-gray-600 text-sm mb-3">Supplier Partnerships</p>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>• 65% local suppliers</div>
                    <div>• 40% diverse suppliers</div>
                    <div>• Fair trade certified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Governance Standards Tab */}
        {activeTab === 'governance' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <span>⚖️</span>
                Governance & Ethical Standards
              </h2>
              <p className="text-purple-100">Upholding transparency, accountability, and ethical business practices</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Board Composition</h3>
                  <span className="text-3xl">👔</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Directors:</span>
                    <span className="font-semibold">12</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Independent:</span>
                    <span className="font-semibold text-green-600">85%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Women:</span>
                    <span className="font-semibold text-blue-600">42%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Diverse Backgrounds:</span>
                    <span className="font-semibold text-purple-600">67%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Avg Tenure:</span>
                    <span className="font-semibold">5.2 years</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Ethics & Compliance</h3>
                  <span className="text-3xl">📜</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Code of Conduct Training:</span>
                    <span className="font-semibold text-green-600">100%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Anti-Corruption Policy:</span>
                    <span className="font-semibold text-green-600">✓ Active</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Whistleblower Hotline:</span>
                    <span className="font-semibold text-green-600">✓ 24/7</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Incidents Reported:</span>
                    <span className="font-semibold">8</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Resolution Rate:</span>
                    <span className="font-semibold text-green-600">100%</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-800">Risk Management</h3>
                  <span className="text-3xl">🛡️</span>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Risk Assessments:</span>
                    <span className="font-semibold">Quarterly</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Identified Risks:</span>
                    <span className="font-semibold">24</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Critical Risks:</span>
                    <span className="font-semibold text-red-600">2</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mitigation Plans:</span>
                    <span className="font-semibold text-green-600">100%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Insurance Coverage:</span>
                    <span className="font-semibold text-green-600">$500M</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>📊</span>
                  Transparency & Disclosure
                </h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">Financial Reporting</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">Excellent</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      • Quarterly earnings calls<br/>
                      • Annual sustainability report<br/>
                      • Real-time disclosures
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">ESG Reporting</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">GRI Aligned</span>
                    </div>
                    <div className="text-xs text-gray-600">
                      • SASB framework<br/>
                      • TCFD recommendations<br/>
                      • CDP disclosure
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <span>🔒</span>
                  Data Privacy & Security
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">GDPR Compliance:</span>
                    <span className="font-bold text-green-600">✓ Certified</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">ISO 27001:</span>
                    <span className="font-bold text-green-600">✓ Certified</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">SOC 2 Type II:</span>
                    <span className="font-bold text-green-600">✓ Certified</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Data Breaches (2024):</span>
                    <span className="font-bold text-green-600">0</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Privacy Training:</span>
                    <span className="font-bold text-green-600">100%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Incident Response Time:</span>
                    <span className="font-bold text-blue-600">&lt; 1hr</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span>⭐</span>
                Certifications & Awards
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { name: 'B Corporation', status: 'Certified', color: 'green' },
                  { name: 'Great Place to Work', status: '2024 Winner', color: 'blue' },
                  { name: 'EcoVadis Gold', status: 'Top 5%', color: 'yellow' },
                  { name: 'CDP A Rating', status: 'Climate Leader', color: 'green' },
                  { name: 'UN Global Compact', status: 'Signatory', color: 'blue' },
                  { name: 'Fair Trade', status: 'Certified', color: 'teal' },
                  { name: 'ISO 14001', status: 'Certified', color: 'green' },
                  { name: 'LEED Platinum', status: '3 Facilities', color: 'emerald' }
                ].map((cert, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 text-center hover:shadow-md transition-shadow">
                    <div className="text-2xl mb-2">🏆</div>
                    <div className="font-semibold text-gray-800 text-sm mb-1">{cert.name}</div>
                    <div className={`text-xs text-${cert.color}-600 font-medium`}>{cert.status}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Score Components Tab */}
        {activeTab === 'components' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Trust Score Components</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Recalculate Scores
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {scoreComponents.map((component, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800">{component.category}</h3>
                    <div className="flex items-center space-x-2">
                      <span className={`text-2xl font-bold ${getGradeColor(component.grade)}`}>
                        {component.currentScore}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        component.grade.startsWith('A') ? 'bg-green-100 text-green-800' :
                        component.grade.startsWith('B') ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {component.grade}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Weight:</span>
                      <span className="font-medium">{component.weight}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Trend:</span>
                      <span className="font-medium flex items-center">
                        {getTrendIcon(component.trend)} {component.trend}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Monthly Change:</span>
                      <span className={`font-medium ${
                        component.monthlyChange > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {component.monthlyChange > 0 ? '+' : ''}{component.monthlyChange}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <h4 className="font-medium text-gray-700">Subcategories:</h4>
                    {component.subcategories?.slice(0, 3).map((sub, subIndex) => (
                      <div key={subIndex} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{sub.name}</span>
                        <span className={`font-medium ${getStatusColor(sub.status)}`}>
                          {sub.score}%
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium text-gray-700 mb-2">Recommendations:</h4>
                    <div className="space-y-1">
                      {component.recommendations?.slice(0, 2).map((rec, recIndex) => (
                        <div key={recIndex} className="text-sm text-gray-600 flex items-start">
                          <span className="mr-2">•</span>
                          <span>{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button 
                    onClick={() => setSelectedComponent(component)}
                    className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    View Details
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risk Assessment Tab */}
        {activeTab === 'risks' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Risk Assessment Matrix</h2>
              <div className="flex gap-2">
                <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  + Add Risk
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Run Assessment
                </button>
              </div>
            </div>

            {/* Risk Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-4 rounded-xl shadow-lg border border-red-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600 mb-1">{riskAssessment.criticalRisks}</div>
                  <div className="text-sm text-gray-600">Critical</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-lg border border-orange-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-1">{riskAssessment.highRisks}</div>
                  <div className="text-sm text-gray-600">High</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-lg border border-yellow-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600 mb-1">{riskAssessment.mediumRisks}</div>
                  <div className="text-sm text-gray-600">Medium</div>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-lg border border-green-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-1">{riskAssessment.lowRisks}</div>
                  <div className="text-sm text-gray-600">Low</div>
                </div>
              </div>
            </div>

            {/* Risk List */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Risk</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Level</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {riskAssessment.risks?.map(risk => (
                      <tr key={risk.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{risk.title}</div>
                          <div className="text-sm text-gray-500">{risk.description}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {risk.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRiskLevelColor(risk.riskLevel)}`}>
                            {risk.riskLevel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            risk.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            risk.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {risk.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {risk.owner}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(risk.dueDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3">View</button>
                          <button className="text-indigo-600 hover:text-indigo-900">Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Compliance Status Tab */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Compliance Framework Status</h2>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{complianceStatus.overallCompliance}%</div>
                <div className="text-sm text-gray-600">Overall Compliance</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {complianceStatus.frameworks?.map((framework, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">{framework.name}</h3>
                      <p className="text-sm text-gray-600">Version {framework.version}</p>
                    </div>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${
                        framework.compliance >= 95 ? 'text-green-600' :
                        framework.compliance >= 85 ? 'text-blue-600' :
                        framework.compliance >= 70 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {framework.compliance}%
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        framework.status === 'Certified' ? 'bg-green-100 text-green-800' :
                        framework.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {framework.status}
                      </span>
                    </div>
                  </div>

                  {framework.controls && (
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-700 mb-2">Controls Summary:</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Implemented:</span>
                          <span className="text-green-600 font-medium">{framework.controls.implemented}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Partial:</span>
                          <span className="text-yellow-600 font-medium">{framework.controls.partiallyImplemented}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Not Implemented:</span>
                          <span className="text-red-600 font-medium">{framework.controls.notImplemented}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-medium">{framework.controls.total}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Audit:</span>
                      <span className="font-medium">{new Date(framework.lastAudit).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next Audit:</span>
                      <span className="font-medium">{new Date(framework.nextAudit).toLocaleDateString()}</span>
                    </div>
                    {framework.auditFirm && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Audit Firm:</span>
                        <span className="font-medium">{framework.auditFirm}</span>
                      </div>
                    )}
                  </div>

                  {framework.gaps && framework.gaps.length > 0 && (
                    <div className="mt-4 border-t pt-4">
                      <h4 className="font-medium text-gray-700 mb-2">Identified Gaps:</h4>
                      <div className="space-y-1">
                        {framework.gaps.map((gap, gapIndex) => (
                          <div key={gapIndex} className="text-sm text-red-600 flex items-start">
                            <span className="mr-2">•</span>
                            <span>{gap}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Security Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Security Performance Metrics</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Incident Metrics */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Incident Response Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Incidents</span>
                    <span className="text-2xl font-bold text-blue-600">{securityMetrics.incidentMetrics?.totalIncidents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Critical Incidents</span>
                    <span className="text-xl font-bold text-red-600">{securityMetrics.incidentMetrics?.criticalIncidents}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Average Resolution Time</span>
                    <span className="font-bold text-green-600">{securityMetrics.incidentMetrics?.averageResolutionTime}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">MTTR</span>
                    <span className="font-bold text-purple-600">{securityMetrics.incidentMetrics?.mttr}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">MTBF</span>
                    <span className="font-bold text-indigo-600">{securityMetrics.incidentMetrics?.mtbf}</span>
                  </div>
                </div>
              </div>

              {/* Vulnerability Metrics */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Vulnerability Management</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Vulnerabilities</span>
                    <span className="text-2xl font-bold text-blue-600">{securityMetrics.vulnerabilityMetrics?.totalVulnerabilities}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Critical Vulnerabilities</span>
                    <span className="text-xl font-bold text-red-600">{securityMetrics.vulnerabilityMetrics?.criticalVulnerabilities}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Avg Time to Remediate</span>
                    <span className="font-bold text-yellow-600">{securityMetrics.vulnerabilityMetrics?.averageTimeToRemediate}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Patching Compliance</span>
                    <span className="font-bold text-green-600">{securityMetrics.vulnerabilityMetrics?.patchingCompliance}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Scan Coverage</span>
                    <span className="font-bold text-blue-600">{securityMetrics.vulnerabilityMetrics?.vulnerabilityScanCoverage}%</span>
                  </div>
                </div>
              </div>

              {/* Access Metrics */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Access Control Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Users</span>
                    <span className="text-2xl font-bold text-blue-600">{securityMetrics.accessMetrics?.totalUsers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Privileged Users</span>
                    <span className="text-xl font-bold text-orange-600">{securityMetrics.accessMetrics?.privilegedUsers}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">MFA Adoption</span>
                    <span className="font-bold text-green-600">{securityMetrics.accessMetrics?.mfaAdoption}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">SSO Adoption</span>
                    <span className="font-bold text-blue-600">{securityMetrics.accessMetrics?.ssoAdoption}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Access Review Compliance</span>
                    <span className="font-bold text-green-600">{securityMetrics.accessMetrics?.accessReviewCompliance}%</span>
                  </div>
                </div>
              </div>

              {/* Data Protection Metrics */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Data Protection Metrics</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Data Classified</span>
                    <span className="text-2xl font-bold text-blue-600">{securityMetrics.dataProtectionMetrics?.dataClassified}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Encryption Coverage</span>
                    <span className="font-bold text-green-600">{securityMetrics.dataProtectionMetrics?.encryptionCoverage}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">DLP Policies</span>
                    <span className="font-bold text-purple-600">{securityMetrics.dataProtectionMetrics?.dlpPolicies}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">DLP Violations</span>
                    <span className="font-bold text-red-600">{securityMetrics.dataProtectionMetrics?.dlpViolations}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Backup Success Rate</span>
                    <span className="font-bold text-green-600">{securityMetrics.dataProtectionMetrics?.backupSuccess}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benchmarks Tab */}
        {activeTab === 'benchmarks' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Industry Benchmarks</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Industry Comparison */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Industry Averages</h3>
                <div className="space-y-4">
                  {Object.entries(benchmarks.industryAverages || {}).map(([industry, scores]) => (
                    <div key={industry} className="border border-gray-200 rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 capitalize mb-3">{industry}</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Trust Score:</span>
                          <span className="font-medium">{scores.trustScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Security:</span>
                          <span className="font-medium">{scores.securityScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Privacy:</span>
                          <span className="font-medium">{scores.privacyScore}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Compliance:</span>
                          <span className="font-medium">{scores.complianceScore}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Peer Comparison */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Peer Comparison</h3>
                <div className="space-y-3">
                  {Object.entries(benchmarks.peerComparison || {}).map(([percentile, scores]) => (
                    <div key={percentile} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                      <span className="font-medium text-gray-800 capitalize">
                        {percentile.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className={`font-bold ${
                          scores.trustScore > trustScoreOverview.overallScore ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {scores.trustScore}
                        </span>
                        <span className={`text-xs ${
                          scores.trustScore > trustScoreOverview.overallScore ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {scores.trustScore > trustScoreOverview.overallScore ? '↑' : '↓'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Your Position */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Your Position</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 mb-1">{trustScoreOverview.percentile}th</div>
                  <div className="text-sm text-gray-600">Industry Percentile</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600 mb-1">
                    {trustScoreOverview.overallScore - benchmarks.industryAverages?.technology?.trustScore > 0 ? '+' : ''}
                    {Math.round(trustScoreOverview.overallScore - benchmarks.industryAverages?.technology?.trustScore)}
                  </div>
                  <div className="text-sm text-gray-600">vs Industry Avg</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600 mb-1">
                    {trustScoreOverview.targetScore - trustScoreOverview.overallScore}
                  </div>
                  <div className="text-sm text-gray-600">Points to Target</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600 mb-1">
                    {trustScoreOverview.confidenceLevel}%
                  </div>
                  <div className="text-sm text-gray-600">Confidence Level</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Improvement Recommendations</h2>

            <div className="space-y-6">
              {recommendations.map((rec) => (
                <div key={rec.id} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-bold text-gray-800">{rec.title}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          rec.priority === 'High' ? 'bg-red-100 text-red-800' :
                          rec.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {rec.priority} Priority
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{rec.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">{rec.estimatedImpact}</div>
                      <div className="text-sm text-gray-600">Estimated Impact</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-600">Effort:</span>
                      <div className="font-medium">{rec.estimatedEffort}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Cost:</span>
                      <div className="font-medium">{rec.costEstimate}</div>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Owner:</span>
                      <div className="font-medium">{rec.owner}</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Action Items:</h4>
                    <div className="space-y-1">
                      {rec.actionItems?.map((item, index) => (
                        <div key={index} className="flex items-start text-sm text-gray-600">
                          <span className="mr-2 text-blue-600">•</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex space-x-2">
                      {rec.complianceFrameworks?.map((framework) => (
                        <span key={framework} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                          {framework}
                        </span>
                      ))}
                    </div>
                    <div className="flex space-x-2">
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                        Accept Recommendation
                      </button>
                      <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Trend Analysis Tab */}
        {activeTab === 'trends' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">Trust Score Trend Analysis</h2>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Historical Performance</h3>
              <div className="space-y-3">
                {trendAnalysis.scoreHistory?.map((point, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded">
                    <span className="font-medium text-gray-800">{point.month}</span>
                    <div className="flex items-center space-x-6 text-sm">
                      <span className="font-bold text-blue-600">{point.score}</span>
                      <span className="text-gray-600">Security: {point.securityScore}</span>
                      <span className="text-gray-600">Privacy: {point.privacyScore}</span>
                      <span className="text-gray-600">Compliance: {point.complianceScore}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Future Projection</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Projected Score:</span>
                    <span className="text-2xl font-bold text-green-600">{trendAnalysis.projectedScore}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Target Date:</span>
                    <span className="font-medium">{new Date(trendAnalysis.projectedDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Improvement Velocity:</span>
                    <span className="font-medium text-blue-600">+{trendAnalysis.improvementVelocity} pts/month</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Seasonal Patterns</h3>
                <div className="space-y-3">
                  {Object.entries(trendAnalysis.seasonalPatterns || {}).map(([quarter, data]) => (
                    <div key={quarter} className="flex justify-between items-center p-3 border border-gray-200 rounded">
                      <span className="font-medium text-gray-800">{quarter.toUpperCase()}</span>
                      <div className="flex items-center space-x-4">
                        <span className="font-bold text-blue-600">{data.averageScore}</span>
                        <span className={`text-sm ${
                          data.trend === 'improving' ? 'text-green-600' :
                          data.trend === 'declining' ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {data.trend}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ESGTrustScoreAnalysis;